import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";
import { normalizeRoleList, pickRoleId } from "../utils/normalizeRole";

const basePath = `${API_URL.idp}/role`;

const tenantHeaders = (tenantId, extra = {}) => ({
  headers: {
    ...(tenantId ? { tenantId: String(tenantId).trim() } : {}),
    ...extra,
  },
});

const getAllTenantRoles = async (tenantId) => {
  return API.get(
    basePath,
    "tenant_roles_list",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantRoleById = async (roleId, tenantId) => {
  if (!roleId) {
    throw new Error("Role id is missing.");
  }

  return API.get(
    basePath,
    "tenant_role_by_id",
    { roleId },
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const getTenantRoleByName = async (roleName, tenantId) => {
  if (!roleName) {
    throw new Error("Role name is missing.");
  }

  return API.get(
    `${basePath}/${encodeURIComponent(roleName)}`,
    "tenant_role_by_name",
    {},
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

/** Postman: POST /role { name } only. Permissions are set via Update. */
const createTenantRole = async (payload = {}, tenantId) => {
  return API.post(
    basePath,
    "tenant_role_create",
    {
      name: String(payload.name || "").trim(),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const updateTenantRole = async (roleId, payload = {}, tenantId) => {
  if (!roleId) {
    throw new Error("Role id is missing.");
  }

  const permissions = Array.isArray(payload.permissions)
    ? payload.permissions
    : Array.isArray(payload.allow)
      ? payload.allow
      : [];

  return API.put(
    `${basePath}/${roleId}`,
    "tenant_role_update",
    {
      permissions: permissions.filter(Boolean).map(String),
    },
    tenantHeaders(tenantId)
  ).then(handleResponse);
};

const deleteTenantRole = async (roleId, tenantId) => {
  if (!roleId) {
    throw new Error("Role id is missing.");
  }

  return API.delete(
    `${basePath}/${roleId}`,
    "tenant_role_delete",
    {},
    tenantHeaders(tenantId, { skipJsonContentType: true })
  ).then(handleResponse);
};

const permissionKeys = (permission) => {
  if (permission == null) return [];
  if (typeof permission === "string") {
    const value = permission.trim().toLowerCase();
    return value ? [value] : [];
  }
  return [
    permission.name,
    permission.permissionId,
    permission.id,
    permission._id,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value, index, list) => value && list.indexOf(value) === index);
};

/**
 * Role APIs store permissions by name. After a permission is deleted, that
 * name can remain on the role and re-bind if a new permission reuses it.
 * Strip the name from every role using Role Id (never role name).
 */
const detachPermissionFromTenantRoles = async (permission, tenantId) => {
  const keys = new Set(permissionKeys(permission));
  if (!keys.size) return;

  const roles = normalizeRoleList(await getAllTenantRoles(tenantId));
  const updates = [];

  roles.forEach((role) => {
    const allow = Array.isArray(role.allow) ? role.allow : [];
    const next = allow.filter(
      (item) => !keys.has(String(item || "").trim().toLowerCase())
    );
    if (next.length === allow.length) return;

    const roleId = pickRoleId(role);
    if (!roleId) return;

    updates.push(
      updateTenantRole(roleId, { permissions: next }, tenantId)
    );
  });

  if (!updates.length) return;
  await Promise.all(updates);
};

export const tenantRoleService = {
  getAllTenantRoles,
  getTenantRoleById,
  getTenantRoleByName,
  createTenantRole,
  updateTenantRole,
  deleteTenantRole,
  detachPermissionFromTenantRoles,
};
